import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import React, { useMemo } from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import { usePendingMagneticWallItem } from '../post/PendingMagneticWallItemProvider';
import getUsersFromPresence from '../post/getUsersFromPresence';
import { useOptimisticItemContentRects } from '../post/useOptimisticItemContentRects';
import Cursor from './Cursor';
import CursorSelection from './CursorSelection';
import { useQuillReadOnly } from './QuillContext';
import TargetHighlight, {
  TargetHighlightWithLastBoundaries,
} from './TargetHighlight';
import type Mark from './utils/Mark';
import getRangeBoundaries from './utils/getRangeBoundaries';

const isCollapsed = (mark: Mark) => {
  switch (mark.type) {
    case 'plain':
      return mark.length === 0;
    case 'cell':
      return mark.cell.length === 0;
    case 'code':
      return mark.code.length === 0;
    default:
      return true;
  }
};

interface MultiCursorProps {
  quill: Quill;
}

const MultiCursor = ({ quill }: MultiCursorProps) => {
  const presence = useAppSelector((state) => state.editor.presence);
  const remoteSelections = useAppSelector(
    (state) => state.editor.remoteSelections,
  );
  const isPickingReaction = useAppSelector(
    (state) => state.editor.isPickingReaction,
  );
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  const readOnly = useQuillReadOnly();
  const users = useMemo(() => {
    const editors = getUsersFromPresence(Object.values(presence))
      .filter(({ status }) => status === 'editing')
      .map(({ user }) => user);
    return _.keyBy(editors, 'id');
  }, [presence]);

  const optimisticItemContentRects = useOptimisticItemContentRects();
  const { pendingCommentThreadRange, pendingAnnotationRange } =
    usePendingMagneticWallItem();

  const newCommentBoundaries =
    pendingCommentThreadRange &&
    getRangeBoundaries(quill, pendingCommentThreadRange);

  const newAnnotationBoundaries =
    pendingAnnotationRange && getRangeBoundaries(quill, pendingAnnotationRange);

  return (
    <>
      {!readOnly &&
        _.map(remoteSelections, (mark, userId) => {
          const user = users[userId];
          if (user == null || mark == null) return false;
          return isCollapsed(mark) ? (
            <Cursor key={userId} quill={quill} user={user} mark={mark} />
          ) : (
            <CursorSelection
              key={userId}
              quill={quill}
              user={user}
              mark={mark}
            />
          );
        })}
      {newCommentBoundaries && (
        <TargetHighlight type="comment" boundaries={newCommentBoundaries} />
      )}
      {newAnnotationBoundaries && (
        <TargetHighlight
          type="annotation"
          boundaries={newAnnotationBoundaries}
        />
      )}
      {((magneticWallFocus &&
        optimisticItemContentRects.has(magneticWallFocus.id)) ||
        isPickingReaction) && (
        <TargetHighlightWithLastBoundaries
          type={magneticWallFocus?.type ?? 'comment'}
        />
      )}
    </>
  );
};

export default MultiCursor;
